import React, { useState } from 'react';
import { useQuery } from 'react-query';
import RestHttpClient from '../../../common/RestHttpClient';
import { buildClassificationGroupMap, ClassificationContext } from './ClassificationContext.types';

export function ClassificationProvider({ children }: { children: React.ReactNode }) {
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const {
    data: featureClassifications = [],
    isLoading: isFeatureClassificationsLoading,
    refetch: refetchFeatureClassification,
  } = useQuery({
    queryKey: ['classification', 'groups', 'features', selectedGroupId],
    queryFn: () => RestHttpClient.getFeatureClassifications(selectedGroupId!),
    select: (x) => x.data,
    enabled: !!selectedGroupId,
  });

  const {
    data: features = [],
    isLoading: isFeatureDefinitionsLoading,
    refetch: refetchFeatureDefinitions,
  } = useQuery({
    queryKey: ['classification', 'feature', 'definitions'],
    queryFn: () => RestHttpClient.getFeatureDefinitions(),
    select: (x) => x.data,
  });

  const {
    data: groups = [],
    refetch: refetchGroups,
    isLoading: isGroupsLoading,
  } = useQuery({
    queryKey: ['classification', 'groups'],
    queryFn: () => RestHttpClient.getClassificationGroups(),
    select: (x) => x.data,
  });

  const groupMap = buildClassificationGroupMap(groups);

  const isFeaturesLoading = isFeatureClassificationsLoading || isFeatureDefinitionsLoading;

  const refetchFeatures = () => {
    refetchFeatureClassification();
    refetchFeatureDefinitions();
  };

  const selectedGroup = groups.find((x) => x.id === selectedGroupId) ?? null;

  return (
    <ClassificationContext.Provider
      value={{
        features,
        featureClassifications,
        isGroupsLoading,
        refetchGroups,
        isFeaturesLoading,
        refetchFeatures,
        selectedGroup,
        setSelectedGroupId,
        groups,
        groupMap,
      }}
    >
      {children}
    </ClassificationContext.Provider>
  );
}
